import { _ } from '@sveltia/i18n';
import { unique } from '@sveltia/utils/array';

import { allEntries } from '$lib/services/contents';
import { selectedCollection } from '$lib/services/contents/collection';
import { getOrderFieldKey } from '$lib/services/contents/collection/entries/reorder/config';
import { isArrayFileCollection } from '$lib/services/contents/collection/predicates';
import { parseCustomSortableFields } from '$lib/services/contents/collection/view/options';
import { getField } from '$lib/services/contents/entry/fields';
import { isNumeric } from '$lib/services/utils/number';
import { createDerivedState } from '$lib/services/utils/state.svelte';

/**
 * @import { InternalEntryCollection, SortingConditions, SortOrder } from '$lib/types/private';
 * @import { Field, FieldKeyPath, NumberField } from '$lib/types/public';
 */

/**
 * Default sort keys for the entry collection. In addition to the `title`, `date`, `author` and
 * `description` fields supported by Netlify/Decap CMS, it also includes `name` for the entry name
 * field, which is used by Sveltia CMS to infer the entry title.
 * @see https://decapcms.org/docs/configuration-options/#sortable_fields
 * @see https://sveltiacms.app/en/docs/collections/entries/views#sorting
 */
export const DEFAULT_SORT_KEYS = ['title', 'name', 'date', 'author', 'description'];

/**
 * Supported sort orders.
 * @type {SortOrder[]}
 */
export const SORT_ORDERS = ['ascending', 'descending'];

/**
 * Common date field keys that require special handling in sorting.
 * @type {string[]}
 */
export const DATE_FIELDS = ['date', 'commit_date'];

/**
 * Map of special sort keys and their types.
 * @type {Record<string, StringConstructor | DateConstructor>}
 */
export const SPECIAL_SORT_KEY_TYPES = {
  slug: String,
  commit_author: String,
  commit_date: Date,
  _summary: String,
  _manual: String,
};

/**
 * List of special sort keys.
 * @type {string[]}
 */
export const SPECIAL_SORT_KEYS = Object.keys(SPECIAL_SORT_KEY_TYPES);

/**
 * Get default sort keys for the entry collection.
 * @param {FieldKeyPath | undefined} customIdField Custom ID field key path.
 * @returns {{ keys: string[], defaultKey?: string, defaultOrder?: SortOrder }} Parsed sortable
 * fields configuration.
 */
export const getDefaultSortKeys = (customIdField) => {
  if (customIdField) {
    // Filter out the custom ID field from DEFAULT_SORT_KEYS to avoid duplicates
    const filteredDefaultKeys = DEFAULT_SORT_KEYS.filter((key) => key !== customIdField);

    return {
      keys: [customIdField, ...filteredDefaultKeys],
      defaultKey: customIdField,
    };
  }

  return { keys: [...DEFAULT_SORT_KEYS] };
};

/**
 * Get sort configuration for the given collection.
 * @param {object} args Arguments.
 * @param {InternalEntryCollection} args.collection Collection.
 * @param {boolean} args.isCommitAuthorAvailable Whether the entries in the collection have a commit
 * author. Available for some Git-based backends.
 * @param {boolean} args.isCommitDateAvailable Whether the entries in the collection have a commit
 * date. Available for some Git-based backends.
 * @returns {{ keys: string[], default: SortingConditions }} A list of sortable fields and default
 * sort conditions.
 */
export const getSortConfig = ({ collection, isCommitAuthorAvailable, isCommitDateAvailable }) => {
  const {
    name: collectionName,
    identifier_field: customIdField,
    sortable_fields: customSortableFields,
    summary: summaryTemplate,
    _i18n: { i18nEnabled, canonicalSlug },
  } = collection;

  const parsed = customSortableFields
    ? parseCustomSortableFields(customSortableFields)
    : getDefaultSortKeys(customIdField);

  // Copy the keys, which can be the configured `sortable_fields` array itself, before adding any
  let keys = [...parsed.keys];
  let { defaultKey, defaultOrder } = parsed;

  // Special handling for summary field: if the collection has a summary template defined, we add
  // `_summary` as a special sort key, which uses the generated summary value
  if (summaryTemplate) {
    keys.unshift('_summary');
  }

  // Make sure the keys are valid field keys or special keys. The canonical slug key,
  // `translationKey` by default, is part of the content of each localized entry without being a
  // field, so it can be used as a sort key when i18n is enabled. This has to come first: the
  // default `date` and `author` keys only stand in for the commit date and author when the
  // collection has such fields
  const specialKeys = i18nEnabled ? [...SPECIAL_SORT_KEYS, canonicalSlug.key] : SPECIAL_SORT_KEYS;

  keys = unique(keys).filter(
    (key) => !!key && (specialKeys.includes(key) || !!getField({ collectionName, keyPath: key })),
  );

  const hasCommitAuthorKey = keys.includes('commit_author');
  const hasCommitDateKey = keys.includes('commit_date');
  // An entry stored in a file with the other entries of the collection carries the file’s last
  // commit, which is about whichever entry was changed last, so it doesn’t tell the entries apart
  const isArrayFile = isArrayFileCollection(collection);

  if (isCommitAuthorAvailable && !isArrayFile) {
    if (!keys.includes('author') && !hasCommitAuthorKey) {
      keys.push('commit_author');
    }
  } else if (hasCommitAuthorKey) {
    keys = keys.filter((key) => key !== 'commit_author');
  }

  if (isCommitDateAvailable && !isArrayFile) {
    if (!keys.includes('date') && !hasCommitDateKey) {
      keys.push('commit_date');
    }
  } else if (hasCommitDateKey) {
    keys = keys.filter((key) => key !== 'commit_date');
  }

  // If the collection allows reordering, expose a single special `_manual` sort key that maps to
  // the order field. We hide the raw order field key from the dropdown — even if the user listed it
  // in `sortable_fields` — to avoid showing two equivalent options.
  const orderKey = getOrderFieldKey(collection);

  // An entry collection storing all the entries in one file keeps them in the order of the array,
  // so the manual order is always available there
  if (orderKey || isArrayFileCollection(collection)) {
    keys = keys.filter((key) => key !== orderKey);

    if (!keys.includes('_manual')) {
      keys.unshift('_manual');
    }

    defaultKey = '_manual';
    defaultOrder = 'ascending';
  }

  defaultKey = defaultKey && keys.includes(defaultKey) ? defaultKey : keys[0];
  defaultOrder ??= defaultKey ? 'ascending' : undefined;

  return {
    keys,
    default: {
      key: defaultKey,
      order: defaultOrder,
    },
  };
};

/**
 * Get the type of the given key, which can be a field key path or one of the entry metadata keys.
 * @param {object} args Arguments.
 * @param {string} args.key Key.
 * @param {Field | undefined} args.fieldConfig Field configuration object.
 * @returns {StringConstructor | BooleanConstructor | NumberConstructor | DateConstructor} Type of
 * the key.
 */
export const getSortKeyType = ({ key, fieldConfig }) => {
  if (key in SPECIAL_SORT_KEY_TYPES) {
    return SPECIAL_SORT_KEY_TYPES[key];
  }

  if (fieldConfig?.widget === 'boolean') {
    return Boolean;
  }

  if (fieldConfig?.widget === 'number') {
    const { value_type: valueType = 'int' } = /** @type {NumberField} */ (fieldConfig);

    if (valueType === 'int' || valueType === 'float') {
      return Number;
    }
  }

  return String;
};

/**
 * Get a field’s label by key.
 * @param {object} args Arguments.
 * @param {InternalEntryCollection} args.collection Collection.
 * @param {FieldKeyPath | string} args.key Field key path or one of other entry metadata property
 * keys: `slug`, `commit_author` and `commit_date`.
 * @returns {string} Label. For a nested field, it would be something like `Name – English`.
 */
export const getSortKeyLabel = ({ collection, key }) => {
  if ([...SPECIAL_SORT_KEYS, 'name'].includes(key)) {
    return _(`sort_keys.${key}`);
  }

  if (key.includes('.')) {
    return key
      .split('.')
      .map((_key, index, arr) => {
        if (isNumeric(_key)) {
          return undefined;
        }

        const keyPath = arr.slice(0, index + 1).join('.');

        // @ts-ignore Hidden field doesn’t have `label` property
        return getField({ collectionName: collection.name, keyPath })?.label || _key;
      })
      .filter(Boolean)
      .join(' – ');
  }

  // @ts-ignore Hidden field doesn’t have `label` property
  return collection.fields?.find(({ name }) => name === key)?.label || key;
};

/**
 * List of available sort keys for the selected entry collection. `getSortKeyLabel()` may return a
 * localized label, and it reads the current app locale, so the list is also recomputed when the
 * locale changes.
 * @type {{ readonly current: { key: string, label: string }[] }}
 */
export const sortKeys = createDerivedState(() => {
  const collection = selectedCollection.current;

  // Disable sorting for file/singleton collection
  if (collection?._type !== 'entry') {
    return [];
  }

  const { current: _allEntries } = allEntries;

  const { keys } = getSortConfig({
    collection,
    isCommitAuthorAvailable: _allEntries.some((entry) => !!entry.commitAuthor),
    isCommitDateAvailable: _allEntries.some((entry) => !!entry.commitDate),
  });

  return keys.map((key) => ({ key, label: getSortKeyLabel({ collection, key }) }));
});
